import React from 'react'
import { Badge, Button, Card, CardContent, CardHeader, Input, Label } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import {
  getModuleHeaderWarningText,
  setModuleHeaderWarningText,
  type HeaderWarningModule,
} from '../services/moduleHeaderWarningSettingsService'

type ModuleHeaderWarningSettingsProps = {
  module: HeaderWarningModule
  label: string
}

export default function ModuleHeaderWarningSettings({
  module,
  label,
}: ModuleHeaderWarningSettingsProps) {
  const { add: toast } = useToast()
  const [text, setText] = React.useState('')
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const mountedRef = React.useRef(true)

  const path =
    module === 'analysis'
      ? '/analise-cripto'
      : module === 'vision'
        ? '/visoes'
        : '/chat'

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      const value = await getModuleHeaderWarningText(module)
      if (!mountedRef.current) return
      setText(value)
    } catch (e: any) {
      if (!mountedRef.current) return
      toast({
        title: `Badge de aviso (${label})`,
        description: e?.message || 'Falha ao carregar texto',
        variant: 'error',
      })
    } finally {
      if (mountedRef.current) setLoading(false)
    }
  }, [label, module, toast])

  React.useEffect(() => {
    mountedRef.current = true
    load()
    return () => {
      mountedRef.current = false
    }
  }, [load])

  async function handleSave() {
    if (saving) return
    setSaving(true)
    try {
      await setModuleHeaderWarningText(module, text)
      toast({
        title: `Badge de aviso (${label})`,
        description: 'Texto atualizado com sucesso.',
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: `Badge de aviso (${label})`,
        description: e?.message || 'Falha ao salvar texto',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  const previewText = text.trim()

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Badge de aviso ({label})</CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-foreground/60">
          Texto exibido abaixo do titulo e subtitulo em {path}. Deixe vazio para ocultar.
        </p>
        <div className="space-y-2">
          <Label className="text-xs text-foreground/70">Texto do badge</Label>
          <Input
            value={text}
            onChange={(e) => setText((e.target as HTMLInputElement).value)}
            placeholder="Ex.: Conteudo exclusivo para assinantes."
            disabled={loading || saving}
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={loading || saving}>
            Salvar
          </Button>
          <Button variant="outline" onClick={load} disabled={loading || saving}>
            Recarregar
          </Button>
        </div>
        {previewText ? (
          <div className="space-y-2">
            <div className="text-xs text-foreground/60">Preview</div>
            <Badge variant="soft" color="warning" className="whitespace-normal">
              {previewText}
            </Badge>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
